/**
 * Report-Strecken: pro Sonderreport eigene Copy an Position 1 (und optional 2)
 * der Welcome-Serie.
 *
 * Die Zuordnung läuft über die LPS-Optin-Mail, NICHT über den Affiliate — ohne
 * `?trk=` fällt der Affiliate auf einen Sammelwert zurück, den sich alle
 * Reports teilen. Beide Auswahlfelder werden aus den LPS-Stammdaten geladen,
 * damit hier niemand eine Id von Hand eintippt.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { api } from '../../helpers/apiUtils';
import ReportFlowCopyEditor from './ReportFlowCopyEditor';

interface Flow {
  id: number;
  name: string;
  opt_in_email_id: number;
  export_configuration_id: number;
  mailing_id_pos1: number | null;
  mailing_id_pos2: number | null;
  active: number;
  subject_pos1: string | null;
  campaign_id_pos1: number | null;
  subject_pos2: string | null;
  campaign_id_pos2: number | null;
}

interface Option {
  id: number;
  label: string;
}

/** Auswählbares Mailing für eine Position — samt der Gründe, aus denen die
 * Scharfschaltung es später ablehnen würde. */
interface MailingOption {
  id: number;
  label: string;
  campaignId: number;
  campaignName: string;
  subject: string;
  productCode: string | null;
  imAutoPick: boolean;
  belegtVonFlow: string | null;
  belegtVonFlowId: number | null;
}

const ReportFlowsTab = () => {
  const [flows, setFlows] = useState<Flow[]>([]);
  const [optins, setOptins] = useState<Option[]>([]);
  const [configs, setConfigs] = useState<Option[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ text: string; severity: 'success' | 'error' } | null>(
    null,
  );
  const [mailingOptions, setMailingOptions] = useState<MailingOption[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  // Gesetzt = der Dialog bearbeitet diese Strecke statt eine neue anzulegen.
  const [editingFlow, setEditingFlow] = useState<Flow | null>(null);
  const [newName, setNewName] = useState('');
  const [newOptin, setNewOptin] = useState<number | ''>('');
  const [newConfig, setNewConfig] = useState<number>(0);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [editor, setEditor] = useState<{
    campaignId: number;
    mailingId: number;
    flowName: string;
    position: number;
  } | null>(null);
  const [creatingKey, setCreatingKey] = useState<string | null>(null);

  /**
   * Nur die Strecken — bewusst NICHT zusammen mit `flow-options`.
   *
   * `flow-options` liest die LPS-Stammdaten aus der LocalDB und ist beim ersten
   * Zugriff nach Leerlauf langsam (in der Sonde vom Host über den gemappten
   * Port: 36 s; auf prod nicht gemessen, dort liegt die DB im selben
   * Container). In einem gemeinsamen `Promise.all` wartete die Tabelle darauf
   * mit — der Reiter blieb leer, obwohl die Strecken längst da waren. Die
   * Optionen braucht nur der Anlegen-Dialog; bis sie da sind, zeigen
   * `optinLabel`/`configLabel` die Ids.
   */
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const flowsRes = await api.get('/api/welcome-campaign/flows');
      setFlows(flowsRes.data?.data || []);
    } catch (error: any) {
      setMessage({
        text: error?.response?.data?.error || 'Laden fehlgeschlagen',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const loadOptions = useCallback(async () => {
    try {
      const optionsRes = await api.get('/api/welcome-campaign/flow-options');
      setOptins(optionsRes.data?.data?.optin_emails || []);
      setConfigs(optionsRes.data?.data?.export_configurations || []);
    } catch (error: any) {
      setMessage({
        text: error?.response?.data?.error || 'Report-Auswahl konnte nicht geladen werden',
        severity: 'error',
      });
    }
  }, []);

  /** Der Mailing-Katalog kommt aus DB14 und ist schnell — trotzdem getrennt,
   * damit er die Tabelle nicht aufhält. */
  const loadMailingOptions = useCallback(async () => {
    try {
      const res = await api.get('/api/welcome-campaign/flow-mailing-options');
      setMailingOptions(res.data?.data || []);
    } catch (error: any) {
      setMessage({
        text: error?.response?.data?.error || 'Mailing-Auswahl konnte nicht geladen werden',
        severity: 'error',
      });
    }
  }, []);

  useEffect(() => {
    load();
    loadOptions();
    loadMailingOptions();
  }, [load, loadOptions, loadMailingOptions]);

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingFlow(null);
    setNewName('');
    setNewOptin('');
    setNewConfig(0);
  };

  const openEditDialog = (flow: Flow) => {
    setEditingFlow(flow);
    setNewName(flow.name);
    setNewOptin(flow.opt_in_email_id);
    setNewConfig(flow.export_configuration_id);
    setDialogOpen(true);
  };

  /** Derselbe Dialog legt an und bearbeitet — die Felder sind dieselben, und
   * zwei getrennte Masken hätten sofort auseinanderdriften können. */
  const saveDialog = async () => {
    setSaving(true);
    try {
      if (editingFlow) {
        await api.put(`/api/welcome-campaign/flows/${editingFlow.id}`, {
          name: newName,
          opt_in_email_id: newOptin,
          export_configuration_id: newConfig,
        });
        setMessage({ text: 'Strecke geändert.', severity: 'success' });
      } else {
        await api.post('/api/welcome-campaign/flows', {
          name: newName,
          opt_in_email_id: newOptin,
          export_configuration_id: newConfig,
        });
        setMessage({ text: 'Strecke angelegt — noch nicht scharf.', severity: 'success' });
      }
      closeDialog();
      await load();
    } catch (error: any) {
      setMessage({
        text: error?.response?.data?.error || 'Speichern fehlgeschlagen',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  /**
   * Löschen. Der Server lehnt zweimal ab: bei scharfer Strecke (erst
   * abschalten) und sobald jemand über sie aufgenommen wurde. Hier nur die
   * Rückfrage, damit ein Fehlklick nichts kostet.
   */
  const remove = async (flow: Flow) => {
    if (flow.active === 1) {
      setMessage({
        text: `„${flow.name}" ist scharf — erst abschalten, dann löschen.`,
        severity: 'error',
      });
      return;
    }
    if (
      !window.confirm(
        `Report-Strecke „${flow.name}" löschen?\n\nDie angelegten Copies bleiben als Entwürfe erhalten. Report-Anmelder bekommen danach die normale Welcome-Serie.`,
      )
    ) {
      return;
    }
    setDeletingId(flow.id);
    try {
      await api.delete(`/api/welcome-campaign/flows/${flow.id}`);
      setMessage({ text: `Strecke „${flow.name}" gelöscht.`, severity: 'success' });
      await load();
      await loadMailingOptions();
    } catch (error: any) {
      setMessage({
        text: error?.response?.data?.error || 'Löschen fehlgeschlagen',
        severity: 'error',
      });
    } finally {
      setDeletingId(null);
    }
  };

  const patch = async (id: number, body: Record<string, unknown>) => {
    try {
      await api.put(`/api/welcome-campaign/flows/${id}`, body);
      await load();
    } catch (error: any) {
      setMessage({
        text: error?.response?.data?.error || 'Änderung fehlgeschlagen',
        severity: 'error',
      });
    }
  };

  /**
   * Legt die Copy für eine Position an und hängt sie an die Strecke. Zwei
   * Schritte mit Absicht: das Anlegen klont die Vorlage, das Verknüpfen läuft
   * über `PUT /flows/:id`, wo die Zielzustands-Prüfung sitzt (Existenz,
   * A/B-Flag, Auto-Pick-Kollision, Betreff-Dedup). Ein Endpunkt, der beides
   * täte, wäre ein zweiter Schreiber auf `mailing_id_posN` — und damit ein
   * Riegel, an dem vorbeigeschrieben werden kann.
   *
   * Scheitert der zweite Schritt, bleibt die Copy als unverknüpfte Kampagne
   * liegen. Das ist der harmlosere Ausgang: sie geht nirgends raus, und die
   * Meldung nennt die Mailing-Id, mit der sie sich von Hand setzen lässt.
   */
  const createCopy = async (flow: Flow, position: 1 | 2) => {
    const key = `${flow.id}-${position}`;
    setCreatingKey(key);
    try {
      const res = await api.post(`/api/welcome-campaign/flows/${flow.id}/copies`, { position });
      const data = res.data?.data;
      if (!data?.campaign_id || !data?.mailing_id) throw new Error('Keine Ids erhalten');

      // Bei einer INAKTIVEN Strecke hat der Endpunkt die Position schon selbst
      // beansprucht — das ist der Riegel gegen zwei gleichzeitige Klicks. Nur
      // bei einer scharfen Strecke bleibt der zweite Schritt nötig, weil dort
      // die Zielzustands-Prüfung von PUT /flows/:id greifen muss.
      if (!data.linked) {
        const field = position === 1 ? 'mailing_id_pos1' : 'mailing_id_pos2';
        try {
          await api.put(`/api/welcome-campaign/flows/${flow.id}`, { [field]: data.mailing_id });
        } catch (linkError: any) {
          setMessage({
            text: `Copy angelegt (Mailing ${data.mailing_id}), aber das Verknüpfen scheiterte: ${
              linkError?.response?.data?.error || linkError?.message
            }`,
            severity: 'error',
          });
          await load();
          return;
        }
      }

      await load();
      setEditor({
        campaignId: data.campaign_id,
        mailingId: data.mailing_id,
        flowName: flow.name,
        position,
      });
    } catch (error: any) {
      setMessage({
        text: error?.response?.data?.error || 'Copy anlegen fehlgeschlagen',
        severity: 'error',
      });
    } finally {
      setCreatingKey(null);
    }
  };

  const optinLabel = (id: number) => optins.find((o) => o.id === id)?.label || `Optin-Mail ${id}`;
  const configLabel = (id: number) =>
    configs.find((c) => c.id === id)?.label || `Konfiguration ${id}`;

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Eine Report-Strecke ersetzt für Anmelder eines bestimmten Sonderreports die Mails auf
        Position 1 und optional 2 durch eigene Copy. Ab Position 3 läuft die Standardstrecke weiter.
        Die Zuordnung entsteht über die Optin-Mail des LPS-Prozesses — der Affiliate taugt nicht
        dafür, er fällt beim Direktaufruf der LP auf einen Sammelwert zurück.
        <br />
        Die Mails werden hier geschrieben — „Copy anlegen" klont die aktuell beste Mail der Position
        als Grundgerüst und öffnet sie im Editor. Es sind PLX-Verkaufsmailings, der Report selbst
        liegt auf der DOI-Seite. Eine Strecke wird erst wirksam, wenn sie scharf geschaltet ist{' '}
        <em>und</em> Position 1 ein Mailing trägt.
      </Alert>

      {message && (
        <Alert severity={message.severity} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Button variant="contained" onClick={() => setDialogOpen(true)}>
          Neue Report-Strecke
        </Button>
        <Button variant="outlined" onClick={load} disabled={loading}>
          Neu laden
        </Button>
      </Stack>

      {flows.length === 0 && !loading ? (
        <Paper sx={{ p: 3 }}>
          <Typography color="text.secondary">
            Noch keine Report-Strecke angelegt. Ohne Strecke bekommen Report-Anmelder die
            gewöhnliche Welcome-Serie — genau wie heute.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Report (Optin-Mail)</TableCell>
                <TableCell>Exportkonfiguration</TableCell>
                <TableCell>Position 1</TableCell>
                <TableCell>Position 2</TableCell>
                <TableCell align="center">Scharf</TableCell>
                <TableCell align="right">Aktion</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {flows.map((flow) => (
                <TableRow key={flow.id} hover>
                  <TableCell>{flow.name}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{optinLabel(flow.opt_in_email_id)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {configLabel(flow.export_configuration_id)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <PositionCell
                      mailingId={flow.mailing_id_pos1}
                      subject={flow.subject_pos1}
                      campaignId={flow.campaign_id_pos1}
                      required
                      busy={creatingKey === `${flow.id}-1`}
                      flowId={flow.id}
                      mailingOptions={mailingOptions}
                      onChange={(value) => patch(flow.id, { mailing_id_pos1: value })}
                      onCreate={() => createCopy(flow, 1)}
                      onEdit={() =>
                        flow.campaign_id_pos1 &&
                        flow.mailing_id_pos1 &&
                        setEditor({
                          campaignId: flow.campaign_id_pos1,
                          mailingId: flow.mailing_id_pos1,
                          flowName: flow.name,
                          position: 1,
                        })
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <PositionCell
                      mailingId={flow.mailing_id_pos2}
                      subject={flow.subject_pos2}
                      campaignId={flow.campaign_id_pos2}
                      busy={creatingKey === `${flow.id}-2`}
                      flowId={flow.id}
                      mailingOptions={mailingOptions}
                      onChange={(value) => patch(flow.id, { mailing_id_pos2: value })}
                      onCreate={() => createCopy(flow, 2)}
                      onEdit={() =>
                        flow.campaign_id_pos2 &&
                        flow.mailing_id_pos2 &&
                        setEditor({
                          campaignId: flow.campaign_id_pos2,
                          mailingId: flow.mailing_id_pos2,
                          flowName: flow.name,
                          position: 2,
                        })
                      }
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip
                      title={
                        flow.mailing_id_pos1
                          ? 'Scharf geschaltet greift die Strecke ab der nächsten Aufnahme'
                          : 'Position 1 braucht ein Mailing, sonst bleibt die Strecke wirkungslos'
                      }
                    >
                      <span>
                        <Switch
                          size="small"
                          checked={flow.active === 1}
                          disabled={!flow.mailing_id_pos1}
                          onChange={(event) => patch(flow.id, { active: event.target.checked })}
                        />
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Tooltip title="Name, Report und Exportkonfiguration ändern">
                        <IconButton size="small" onClick={() => openEditDialog(flow)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Strecke löschen — geht nur, solange niemand über sie aufgenommen wurde">
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            disabled={deletingId === flow.id}
                            onClick={() => remove(flow)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {editor && (
        <ReportFlowCopyEditor
          open
          onClose={() => {
            setEditor(null);
            load();
          }}
          campaignId={editor.campaignId}
          mailingId={editor.mailingId}
          flowName={editor.flowName}
          position={editor.position}
          onSaved={load}
        />
      )}

      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>
          {editingFlow ? `Report-Strecke „${editingFlow.name}" bearbeiten` : 'Neue Report-Strecke'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {editingFlow && editingFlow.active === 1 && (
              <Alert severity="warning">
                Die Strecke ist scharf. Ein anderer Report hier bedeutet: ab sofort bekommen dessen
                Anmelder diese Copy — bereits aufgenommene behalten ihre eingefrorene Serie.
              </Alert>
            )}
            {optins.length === 0 && (
              // Die Optionen kommen aus der LocalDB und laden getrennt von der
              // Tabelle. Ohne diesen Hinweis sähe der Dialog nur nach einer
              // leeren Auswahl aus.
              <Alert severity="info">
                LPS-Stammdaten werden geladen — die Report-Auswahl füllt sich gleich.
              </Alert>
            )}
            <TextField
              label="Name"
              value={newName}
              onChange={(event) => setNewName(event.target.value)}
              placeholder="z. B. Sonderreport Die neue Nvidia"
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel id="flow-optin-label">Report (LPS-Optin-Mail)</InputLabel>
              <Select
                labelId="flow-optin-label"
                label="Report (LPS-Optin-Mail)"
                value={newOptin}
                onChange={(event) => setNewOptin(Number(event.target.value))}
              >
                {optins.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="flow-config-label">Exportkonfiguration</InputLabel>
              <Select
                labelId="flow-config-label"
                label="Exportkonfiguration"
                value={newConfig}
                onChange={(event) => setNewConfig(Number(event.target.value))}
              >
                {configs.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary">
              Die Exportkonfiguration ist ein Zusatzfilter. „Beliebig“ reicht, solange die
              Optin-Mail nur für einen Report benutzt wird.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Abbrechen</Button>
          <Button
            variant="contained"
            onClick={saveDialog}
            disabled={saving || !newName || !newOptin}
          >
            {editingFlow ? 'Speichern' : 'Anlegen'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const PositionCell = ({
  mailingId,
  subject,
  campaignId,
  required,
  busy,
  mailingOptions,
  flowId,
  onChange,
  onCreate,
  onEdit,
}: {
  mailingId: number | null;
  subject: string | null;
  campaignId: number | null;
  required?: boolean;
  busy?: boolean;
  mailingOptions: MailingOption[];
  /** Eigene Strecke — ihre Belegungen sind keine fremden. */
  flowId: number;
  onChange: (value: number | null) => void;
  onCreate: () => void;
  onEdit: () => void;
}) => {
  const [picking, setPicking] = useState(false);

  /**
   * Gruppe eines Eintrags — die EINE Regel, aus der sowohl `groupBy` als auch
   * die Sortierung unten abgeleitet werden.
   *
   * Sie muss hier liegen und nicht im Service: nur hier ist bekannt, für welche
   * Strecke das Feld geöffnet wurde, und eine Belegung durch die eigene Strecke
   * ist keine fremde. Als der Service noch allein sortierte und die Oberfläche
   * bereits flow-bewusst gruppierte, fielen beide auseinander und „Verfügbar"
   * erschien zweimal in derselben Liste — MUIs `groupBy` fasst nur
   * AUFEINANDERFOLGENDE Einträge zusammen.
   */
  const gruppe = (o: MailingOption): 0 | 1 | 2 =>
    o.imAutoPick ? 1 : o.belegtVonFlow && o.belegtVonFlowId !== flowId ? 2 : 0;

  const gruppenName = [
    'Verfügbar',
    'Steht im aktiven Auto-Pick — nicht scharfschaltbar',
    'Belegt von einer anderen Strecke',
  ];

  const sortierteOptionen = [...mailingOptions].sort(
    (a, b) => gruppe(a) - gruppe(b) || b.id - a.id,
  );

  if (picking) {
    // Auswahl statt Eingabe: eine getippte Mailing-Id war fehleranfällig und
    // gab erst beim Scharfschalten Rückmeldung. Die Liste zeigt die Gründe,
    // aus denen die Aktivierung ein Mailing ablehnen würde, gleich mit — dann
    // sucht niemand ein Mailing, das absichtlich nicht wählbar ist.
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <Autocomplete
          size="small"
          // Schmal in der Zelle, breit im Aufklapper: 380 px inline haben die
          // Report-Spalte auf sieben Zeilen gequetscht und der Tabelle eine
          // Querleiste verpasst. Die Liste braucht die Breite, das Feld nicht.
          sx={{ width: 230 }}
          slotProps={{ paper: { sx: { width: 560 } } }}
          options={sortierteOptionen}
          autoHighlight
          openOnFocus
          value={mailingOptions.find((o) => o.id === mailingId) ?? null}
          getOptionLabel={(o) => o.subject}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          groupBy={(o) => gruppenName[gruppe(o)]}
          renderOption={(props, o) => (
            <li {...props} key={o.id}>
              <Stack sx={{ minWidth: 0 }}>
                <Typography variant="body2" noWrap>
                  {o.subject}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {o.campaignName}
                  {o.productCode ? ` · ${o.productCode}` : ''} · Mailing {o.id}
                  {o.belegtVonFlow && o.belegtVonFlowId !== flowId
                    ? ` · belegt von „${o.belegtVonFlow}"`
                    : ''}
                </Typography>
              </Stack>
            </li>
          )}
          renderInput={(params) => (
            <TextField {...params} label="Mailing wählen" placeholder="Betreff suchen …" />
          )}
          onChange={(_event, value) => {
            // Leeren = bewusstes Zurücksetzen auf Auto-Pick.
            onChange(value ? value.id : null);
            setPicking(false);
          }}
        />
        <Button size="small" onClick={() => setPicking(false)}>
          Abbrechen
        </Button>
      </Stack>
    );
  }

  if (!mailingId) {
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <Chip
          size="small"
          variant="outlined"
          color={required ? 'warning' : 'default'}
          label={required ? 'fehlt' : 'Auto-Pick'}
        />
        <Button size="small" variant="outlined" disabled={busy} onClick={onCreate}>
          {busy ? 'legt an …' : 'Copy anlegen'}
        </Button>
        <Tooltip title="Stattdessen auf ein bestehendes Mailing zeigen">
          <Button size="small" onClick={() => setPicking(true)}>
            Mailing wählen
          </Button>
        </Tooltip>
      </Stack>
    );
  }

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Tooltip title={subject || `Mailing ${mailingId}`}>
        <Typography variant="body2" sx={{ maxWidth: 220 }} noWrap>
          {subject || `Mailing ${mailingId}`}
        </Typography>
      </Tooltip>
      {campaignId && (
        <Button size="small" variant="outlined" onClick={onEdit}>
          bearbeiten
        </Button>
      )}
      <Tooltip title="Auf ein anderes Mailing zeigen">
        <Button size="small" onClick={() => setPicking(true)}>
          wechseln
        </Button>
      </Tooltip>
      {campaignId && (
        <Tooltip title="Im PLX-Editor öffnen (Notausgang — Produkt, Landing-URL, WKZ)">
          <a
            href={`/plx-mailings?tab=editor&id=${campaignId}`}
            target="_blank"
            rel="noreferrer"
            style={{ display: 'inline-flex' }}
          >
            <OpenInNewIcon fontSize="small" />
          </a>
        </Tooltip>
      )}
    </Stack>
  );
};

export default ReportFlowsTab;
